import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { reportBondStepValidationErrors } from "../bondWizardStepHelpers";
import {
  BOND_FORM_STEPS,
  buildBondFormInitialState,
  buildBondFormInitialStep,
} from "../bondFormConfig";
import {
  loadBondCacheFromParams,
  mergeBondStepState,
  saveBondFormProgress,
  submitBondSupportRequest,
  validateStepClickNavigation,
} from "../bondFormBaseHelpers";
export {
  runDuplicateBondCheck,
  finalizeBondCreation,
} from "../bondFormCreationHelpers";
export { createBondKindLogic, useStandardBondKindLogic } from "./useBondKindLogic";

/**
 * @typedef {import("../bondFormConfig").BondFormKindConfig} BondFormKindConfig
 */

/**
 * @typedef {import("../bondFormCreationHelpers").BondFormBaseContext} BondFormBaseContext
 */

/**
 * @param {{
 *   config: BondFormKindConfig,
 *   logContext: string,
 *   useBondKindLogic: (ctx: BondFormBaseContext) => import("./useBondKindLogic").BondFormKindLogic,
 * }} options
 */
export default function useBondFormBase({ config, logContext, useBondKindLogic }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { cache_id } = useParams();

  const buildInitialState = useCallback(
    () => buildBondFormInitialState(config.basicInfoDefaults, logContext),
    [config.basicInfoDefaults, logContext]
  );

  const [activeStep, setActiveStep] = useState(() =>
    buildBondFormInitialStep(logContext)
  );
  const [globalState, setGlobalState] = useState(buildInitialState);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cacheId, setCacheId] = useState(cache_id || "");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateBondId, setDuplicateBondId] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [failedBondType, setFailedBondType] = useState(config.failedBondType);

  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);

  const [isGuestRequest, setIsGuestRequest] = useState(() =>
    (location.pathname || "").startsWith(config.guestPathPrefix)
  );

  const userdetails = useSelector((state) => state.auth.user);
  const lawfirmdetails = useSelector((state) => state.lawfirm);
  const { role } = useSelector((state) => state.auth);

  const ctx = useMemo(
    () => ({
      config,
      globalState,
      setGlobalState,
      activeStep,
      setActiveStep,
      setErrors,
      isGuestRequest,
      isLoading,
      setIsLoading,
      cacheId,
      setCacheId,
      cache_id,
      userdetails,
      lawfirmdetails,
      role,
      navigate,
      setSubmissionError,
      setFailedBondType,
      setSupportModalOpen,
      setIsDuplicateDialogOpen,
      setDuplicateBondId,
      setPendingPayload,
      buildInitialState,
    }),
    [
      config,
      globalState,
      activeStep,
      isGuestRequest,
      isLoading,
      cacheId,
      cache_id,
      userdetails,
      lawfirmdetails,
      role,
      navigate,
      buildInitialState,
    ]
  );

  const { validateStepData, handleFinish, createBond } = useBondKindLogic(ctx);

  useEffect(() => {
    if (localStorage.getItem("resumedBondPayload")) {
      toast.info("Restored bond application from support request.");
      localStorage.removeItem("resumedBondPayload");
    }
  }, []);

  useEffect(() => {
    setIsGuestRequest((location.pathname || "").startsWith(config.guestPathPrefix));
  }, [location.pathname, config.guestPathPrefix]);

  useEffect(() => {
    if (!cache_id) return;
    loadBondCacheFromParams(cache_id, logContext, setGlobalState, setActiveStep);
  }, [cache_id, logContext]);

  useEffect(() => {
    if (typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activeStep]);

  const updateGlobalState = useCallback((stepKey, stepData) => {
    setGlobalState((prev) => ({
      ...prev,
      [stepKey]: mergeBondStepState(stepKey, stepData),
    }));
    Object.keys(stepData).forEach((fieldName) => {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    });
  }, []);

  const handleNext = useCallback(() => {
    const currentStepData = globalState[Object.keys(globalState)[activeStep]];
    const currentStepErrors = validateStepData(currentStepData, activeStep, globalState);

    if (Object.keys(currentStepErrors).length > 0) {
      setErrors(currentStepErrors);
      reportBondStepValidationErrors({ currentStepErrors, activeStep });
      return;
    }

    if (activeStep === BOND_FORM_STEPS.length - 1) {
      handleFinish();
    } else {
      setActiveStep((prev) => prev + 1);
      setErrors({});
    }
  }, [activeStep, globalState, validateStepData, handleFinish]);

  const handleBack = useCallback(() => setActiveStep((prev) => prev - 1), []);

  const handleStepClick = useCallback(
    (stepIndex) => {
      validateStepClickNavigation({
        stepIndex,
        activeStep,
        globalState,
        validateStepData,
        setErrors,
        setActiveStep,
      });
    },
    [activeStep, globalState, validateStepData]
  );

  const handleSave = useCallback(
    () =>
      saveBondFormProgress(config, {
        userdetails,
        cacheId,
        cache_id,
        activeStep,
        globalState,
        navigate,
        setCacheId,
        setIsSaving,
      }),
    [config, userdetails, cacheId, cache_id, activeStep, globalState, navigate]
  );

  const handleSupportRequestSubmit = useCallback(
    async (description) => {
      await submitBondSupportRequest(config, {
        activeStep,
        globalState,
        failedBondType,
        description,
        navigate,
        role,
        logContext,
        setSupportModalOpen,
      });
    },
    [config, activeStep, globalState, failedBondType, navigate, role, logContext]
  );

  const handleDuplicateProceed = useCallback(async () => {
    setIsDuplicateDialogOpen(false);
    if (pendingPayload) {
      setIsLoading(true);
      await createBond(pendingPayload);
      setPendingPayload(null);
      setDuplicateBondId(null);
    }
  }, [createBond, pendingPayload]);

  const handleDuplicateCancel = useCallback(() => {
    setIsDuplicateDialogOpen(false);
    setPendingPayload(null);
    setDuplicateBondId(null);
  }, []);

  return {
    steps: BOND_FORM_STEPS,
    activeStep,
    setActiveStep,
    globalState,
    errors,
    setErrors,
    isLoading,
    isSaving,
    isGuestRequest,
    isDialogOpen,
    setIsDialogOpen,
    isDuplicateDialogOpen,
    duplicateBondId,
    supportModalOpen,
    setSupportModalOpen,
    submissionError,
    failedBondType,
    openCalculator,
    setOpenCalculator,
    openSupport,
    setOpenSupport,
    userdetails,
    lawfirmdetails,

    updateGlobalState,
    handleNext,
    handleBack,
    handleStepClick,
    handleSave,
    handleFinish,
    handleSupportRequestSubmit,
    handleDuplicateProceed,
    handleDuplicateCancel,
    createBond,
  };
}
